import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingBag } from 'lucide-react';
import { useCart } from '@context/CartContext';
import { ROUTES } from '@utils/constants';
import Layout from '@components/layout/Layout';
import Button from '@components/common/Button';
import CartItem from '@components/cart/CartItem';
import CartSummary from '@components/cart/CartSummary';

const CartPage: React.FC = () => {
  const { items, loading } = useCart();
  const navigate = useNavigate();

  const handleCheckout = () => {
    navigate(ROUTES.CHECKOUT);
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              icon={<ArrowLeft size={16} />}
              className="text-gray-600 hover:text-gray-900"
            >
              Continue Shopping
            </Button>
          </div>

          <h1 className="text-3xl font-bold text-gray-900 mb-2">Shopping Cart</h1>
          <p className="text-gray-600">Review your selected photos before checkout</p>
        </div>

        {items.length === 0 ? (
          /* Empty Cart */
          <div className="text-center py-16">
            <div className="max-w-md mx-auto">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <ShoppingBag className="w-8 h-8 text-gray-400" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Your cart is empty</h2>
              <p className="text-gray-600 mb-8">
                Discover amazing photos from our featured events and start building your collection.
              </p>
              <div className="space-y-3">
                <Link to={ROUTES.EVENTS} className="block">
                  <Button variant="primary" size="large" className="w-full">
                    Browse Events
                  </Button>
                </Link>
                <Link to={ROUTES.HOME} className="block">
                  <Button variant="outline" size="large" className="w-full">
                    Go to Home
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        ) : (
          /* Cart with Items */
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Cart Items ({items.length})
                </h2>

                <div className="space-y-4">
                  {items.map(item => (
                    <CartItem key={item.id} item={item} />
                  ))}
                </div>
              </div>

              {/* Continue Shopping */}
              <div className="flex justify-between items-center">
                <Link to={ROUTES.EVENTS}>
                  <Button variant="ghost" icon={<ArrowLeft size={16} />}>
                    Continue Shopping
                  </Button>
                </Link>

                {/* Additional actions */}
                <div className="flex gap-2">
                  {/* TODO: Add save for later functionality */}
                  <Button variant="outline" size="small" disabled>
                    Save for Later
                  </Button>
                  {/* TODO: Add create wishlist functionality */}
                  <Button variant="outline" size="small" disabled>
                    Create Wishlist
                  </Button>
                </div>
              </div>
            </div>

            {/* Cart Summary */}
            <div className="lg:col-span-1">
              <CartSummary showCheckoutButton={true} onCheckout={handleCheckout} />

              {/* Security badges */}
              <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-green-800 mb-2">Secure Checkout</h3>
                <div className="space-y-1 text-xs text-green-700">
                  <div className="flex items-center gap-1">
                    <span>🔒</span>
                    <span>256-bit SSL encryption</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span>💳</span>
                    <span>PCI DSS compliant</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span>⚡</span>
                    <span>Instant download after payment</span>
                  </div>
                </div>
              </div>

              {/* Money-back guarantee */}
              <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-blue-800 mb-2">
                  100% Satisfaction Guarantee
                </h3>
                <p className="text-xs text-blue-700">
                  Not satisfied with your purchase? Get a full refund within 30 days, no questions
                  asked.
                </p>
              </div>

              {/* Support */}
              <div className="mt-4 text-center">
                <p className="text-xs text-gray-500 mb-2">Need help with your order?</p>
                <Button variant="ghost" size="small">
                  Contact Support
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Loading overlay */}
        {loading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6">
              <div className="flex items-center gap-3">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                <span>Updating cart...</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default CartPage;
